import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {ref, set, onValue, remove, get, update} from 'firebase/database';
import { onValueThrottled } from '../utils/rtdbThrottle';
import { uploadFileToWasabi, deleteFileFromWasabi } from '../utils/wasabiUploader';
import {
  FiUpload,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiMonitor,
  FiUsers,
  FiCheckSquare,
  FiSquare,
  FiChevronDown,
  FiChevronUp,
  FiRotateCcw,
  FiUploadCloud,
  FiSmartphone, FiEdit
} from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {formatBytes} from "./AppsPage";
import CustomDropdown from "../components/CustomDropdown";

export default function OTAUpdatePage() {
  const [otaFile, setOtaFile] = useState(null);
  const [version, setVersion] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [updateType, setUpdateType] = useState('optional'); // 'optional' or 'major'
  const [updateState, setUpdateState] = useState('ENABLED'); // 'ENABLED' or 'DISABLED'
  const [otaList, setOtaList] = useState([]);
  const [devices, setDevices] = useState({});
  const [groupList, setGroupList] = useState({});
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [selectedOta, setSelectedOta] = useState(null);
  const [actionTarget, setActionTarget] = useState('selected'); // 'selected' or group name
  // const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const otasPerPage = 5;

  const [editingOta, setEditingOta] = useState(null);
  const [editVersion, setEditVersion] = useState('');
  const [editDeviceModel, setEditDeviceModel] = useState('');
  const [editOtaUpdateType, setEditOtaUpdateType] = useState('optional'); // 'optional' or 'major'
  const [editOtaUpdateState, setEditOtaUpdateState] = useState('ENABLED'); // 'ENABLED' or 'DISABLED'

  const [editOtaFileSize, setEditOtaFileSize] = useState(0);
  const [editOtaFile, setEditOtaFile] = useState(null);

  const [loadingEditMetadata, setLoadingEditMetadata] = useState(false);

  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    const otaRef = ref(db, 'mdm/ota');
    const devicesRef = ref(db, 'mdm/devices');
    const groupListRef = ref(db, 'mdm/groups');

    const unsubscribeOta = onValue(
      otaRef,
      (snapshot) => {
        try {
          const data = snapshot.val() || {};
          const entries = Object.entries(data).map(([model, info]) => ({
            deviceModel:    model,           // <-- the key!
            version:        info.version,
            url:            info.url,
            updateType:     info.updateType   || 'optional',
            updateState:    info.updateState  || 'ENABLED',
            timestamp:      info.timestamp     || new Date().toISOString(),
          }));
          setOtaList(entries);
          // setLoading(false);
        } catch (err) {
          toast.error('Failed to load OTA versions: ' + err.message);
          // setLoading(false);
        }
      },
      (err) => {
        toast.error('Failed to fetch OTA versions: ' + err.message);
        // setLoading(false);
      }
    );

    const unsubscribeDevices = onValueThrottled(
      devicesRef,
      (snapshot) => {
        try {
          const data = snapshot.val() || {};
          setDevices(data);
        } catch (err) {
          toast.error('Failed to load devices: ' + err.message);
        }
      },
      (err) => {
        toast.error('Failed to fetch devices: ' + err.message);
      }
    );

    const unsubscribeGroupList = onValue(
      groupListRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        setGroupList(data);
      },
      (err) => {
        toast.error('Failed to fetch group list: ' + err.message);
      }
    );

    return () => {
      unsubscribeOta();
      unsubscribeDevices();
      unsubscribeGroupList();
    };
  }, []);

  function compareVersions(v1, v2) {
    // Ensure both values are strings
    const strA = String(v1 || '0');
    const strB = String(v2 || '0');
    const a = strA.split('.').map(n => parseInt(n, 10) || 0);
    const b = strB.split('.').map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      const na = a[i] || 0, nb = b[i] || 0;
      if (na > nb) return 1;
      if (na < nb) return -1;
    }
    return 0;
  }

  const deviceModels = React.useMemo(() => {
    const models = new Set()
    Object.values(devices).forEach(d => {
      if (d.device_model) models.add(d.device_model)
    })
    return Array.from(models).sort()
  }, [devices])

  useEffect(() => {
    if (deviceModels.length === 1) {
      setDeviceModel(deviceModels[0]);
    }
  }, [deviceModels]);

  const [otaUploading, setOtaUploading] = useState(false);
  const [otaEditUploading, setOtaEditUploading] = useState(false);

  const handleUpload = async () => {
    if (!otaFile || !version || !deviceModel) {
      toast.error('Please select an OTA .zip file, specify a version, and select a device model.');
      return;
    }
    const otaRef = ref(db, `mdm/ota/${deviceModel}`);

    try {
      // 1) see if there's already an entry for this model
      const snap = await get(otaRef);
      if (snap.exists()) {
        const existing = snap.val();
        if (compareVersions(version, existing.version) <= 0) {
          toast.error(
              `Existing OTA for ${deviceModel} is version ${existing.version}. ` +
              'Please upload a higher version.'
          );
          return;
        }
      }

      // 2) upload the file
      const uploadPath = `ota/${deviceModel}-${version}-${Date.now()}`;
      setOtaUploading(true);
      const { publicUrl } = await uploadFileToWasabi(otaFile, uploadPath);
      setOtaUploading(false);
      const url = publicUrl;

      // 3) write only the fields we care about
      await set(otaRef, {
        url,
        version,
        updateType,
        updateState,
        timestamp: Date.now(),
      }); // 4) reset
      setOtaFile(null);
      setVersion('');
      setDeviceModel('');
      setUpdateType('optional');
      setUpdateState('ENABLED');
      toast.success(`OTA for ${deviceModel} updated to version ${version}.`);
    } catch (err) {
      setOtaUploading(false);
      toast.error('Failed to upload OTA package: ' + err.message);
    }
  };

  async function deleteOtaVersion(ota) {
    const {version, model} = ota

    toast.info(`Deleting OTA version ${version} for ${model} device…`);
    try {
      const otaRef = ref(db, `mdm/ota/${model}`);

      const snap = await get(otaRef);
      if (snap.exists()) {
        const existing = snap.val();
        const url = existing.url;
        if (url) {
          try {
            await deleteFileFromWasabi(url);
          } catch (wasabiErr) {
            // Log warning but continue - file might be in different bucket or already deleted
            console.warn('Could not delete file from Wasabi (continuing anyway):', wasabiErr.message);
          }
        }
      }

      //remove just the ota
      await remove(otaRef)

      toast.success(`OTA version ${version} removed.`);
    } catch (err) {
      toast.error('Failed to delete OTA: ' + (err.message || 'Unknown error'));
    }
  }

  const openEditModal = (row) => {
    setEditingOta(row);

    setEditVersion(row.version);
    setEditOtaUpdateType(row.updateType);
    setEditOtaUpdateState(row.updateState);

    setEditDeviceModel(row.deviceModel);

    setEditOtaFile(null);
  }

  const toggleUpdateState = async (ota) => {
    const newState = ota.updateState === 'ENABLED' ? 'DISABLED' : 'ENABLED';
    try {
      const otaRef = ref(db, `mdm/ota/${ota.deviceModel}`);
      await update(otaRef, { updateState: newState });
      toast.success(`OTA version "${ota.version}" is now ${newState.toLowerCase()}.`);
    } catch (err) {
      toast.error('Failed to toggle OTA state: ' + err.message);
    }
  };

  const pushOtaToDevices = async (ota, groupName = null, deviceIds = null) => {
    let targets = [];
    if (groupName) {
      targets = Object.keys(devices).filter(
        (deviceId) => devices[deviceId].group === groupName
      );
    } else if (deviceIds) {
      targets = deviceIds;
    } else {
      targets = selectedDevices.length > 0 ? selectedDevices : Object.keys(devices);
    }

    if (targets.length === 0) {
      toast.error('No devices available to push the OTA update to.');
      return;
    }

    const promises = targets.map(async (deviceId) => {
      const otaRef = ref(db, `mdm/devices/${deviceId}/command`);
      await set(otaRef, {
        command: 'ota_update',
        url: ota.url.publicUrl, // Use publicUrl for the device command
        version: ota.version,
      });
      setTimeout(() => set(otaRef, null), 10000);
    });

    try {
      await Promise.all(promises);
      toast.success(`OTA update (version ${ota.version}) pushed to ${targets.length} device(s).`);
    } catch (err) {
      toast.error('Failed to push OTA: ' + err.message);
    }
  };

  const handleClearOtaEditState = () => {
    setEditingOta(null)
    setEditOtaFile(null)
    setEditOtaFileSize(0)
    setEditVersion('')
    setEditDeviceModel('')
    setEditOtaUpdateState('ENABLED')
    setEditOtaUpdateType('optional')
  }

  const saveEditedOta = async () => {
    if(!editingOta) return;

    const oldModel = editingOta.deviceModel;
    const newModel = editDeviceModel;

    let otaUrl = ''
    try {
      if(editOtaFile) {
        setOtaEditUploading(true);

        let deviceModel = ''
        if (oldModel !== newModel) {
          deviceModel = newModel
        } else {
          deviceModel = oldModel
        }
        const uploadPath = `ota/${deviceModel}-${editVersion}-${Date.now()}`;
        const { publicUrl } = await uploadFileToWasabi(editOtaFile, uploadPath);
        otaUrl = publicUrl;

        if (editingOta.url) await deleteFileFromWasabi(editingOta.url)
        setOtaEditUploading(false);
      } else {
        otaUrl = editingOta.url;
      }

      if (oldModel !== newModel) {
        const oldSnap = await get(ref(db, `mdm/ota/${oldModel}`));
        const {timestamp, updateState, updateType, url, version} = oldSnap.val()

        await set(ref(db, `mdm/ota/${newModel}`), {
          timestamp: Date.now(),
          updateState: editOtaUpdateState,
          updateType: editOtaUpdateType,
          url: otaUrl,
          version: editVersion
        })

        await remove(ref(db, `mdm/ota/${oldModel}`))
      } else {
        await update(ref(db, `mdm/ota/${oldModel}`), {
          timestamp: Date.now(),
          updateState: editOtaUpdateState,
          updateType: editOtaUpdateType,
          url: otaUrl,
          version: editVersion
        })
      }
      toast.success('OTA updated successfully!');
      setEditingOta(null);
    } catch (err) {
      setOtaUploading(false)
      toast.error('Failed to save changes: ' + err.message);
    }
  }

  const rollbackToVersion = async (version, groupName = null, deviceIds = null) => {
    const rollbackEntry = otaList.find((o) => o.version === version);
    if (!rollbackEntry) {
      toast.error('Version not found');
      return;
    }
    await pushOtaToDevices(rollbackEntry, groupName, deviceIds);
  };

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
    toast.info(`Device ${deviceId} ${selectedDevices.includes(deviceId) ? 'deselected' : 'selected'}.`);
  };

  const selectAllDevices = () => {
    setSelectedDevices(Object.keys(devices));
    toast.success('All devices selected.');
  };

  const clearAllDevices = () => {
    setSelectedDevices([]);
    toast.success('Device selection cleared.');
  };

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const selectGroupDevices = (groupName) => {
    const groupDevices = Object.keys(devices).filter(
      (deviceId) => devices[deviceId].group === groupName
    );
    setSelectedDevices((prev) => {
      const newSelection = [...new Set([...prev, ...groupDevices])];
      toast.success(`Selected all devices in group "${groupName}".`);
      return newSelection;
    });
  };

  const deselectGroupDevices = (groupName) => {
    const groupDevices = Object.keys(devices).filter(
      (deviceId) => devices[deviceId].group === groupName
    );
    setSelectedDevices((prev) => {
      const newSelection = prev.filter((deviceId) => !groupDevices.includes(deviceId));
      toast.success(`Deselected all devices in group "${groupName}".`);
      return newSelection;
    });
  };

  const indexOfLastOta = currentPage * otasPerPage;
  const indexOfFirstOta = indexOfLastOta - otasPerPage;
  const currentOtas = otaList.slice(indexOfFirstOta, indexOfLastOta);
  const totalPages = Math.ceil(otaList.length / otasPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const hasModels = (deviceModels?.length ?? 0) > 0;


  // if (loading) {
  //   return (
  //     <div className="p-6 bg-gray-900 text-white min-h-screen flex items-center justify-center">
  //       <p className="text-xl">Loading...</p>
  //     </div>
  //   );
  // }

  // if (error) {
  //   return (
  //     <div className="p-6 bg-gray-900 text-white min-h-screen flex items-center justify-center">
  //       <p className="text-red-400 text-xl">{error}</p>
  //     </div>
  //   );
  // }

  return (
    <div className="page-shell">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="brand-icon-badge"><FiUploadCloud /></div>
        <h1 className="text-xl font-semibold text-white">OTA Update Management</h1>
      </div>

      {/* Upload */}
      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><FiUpload /></div>
          <h2 className="text-sm font-semibold text-white">Upload New OTA Version</h2>
        </header>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">
                  Version
                </label>
                <input
                  type="text"
                  placeholder="e.g. 1.0.0"
                  value={version}
                  onChange={e => setVersion(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <CustomDropdown
                  label="Device Model"
                  value={deviceModel}
                  onChange={setDeviceModel}
                  options={deviceModels.map(model => ({ value: model, label: model }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CustomDropdown
                label="Update Is"
                value={updateType}
                onChange={setUpdateType}
                options={[
                  { label: 'Optional', value: 'optional' },
                  { label: 'Major', value: 'major' },
                ]}
              />
              <CustomDropdown
                label="Availability"
                value={updateState}
                onChange={setUpdateState}
                options={[
                  { label: 'Enabled', value: 'ENABLED' },
                  { label: 'Disabled', value: 'DISABLED' },
                ]}
              />
            </div>
          </div>

          <div className="flex flex-col items-center justify-center lg:w-1/3 space-y-3">
            <label className="btn-secondary cursor-pointer w-full max-w-xs">
              <FiUpload /> Choose .zip
              <input
                type="file"
                accept=".zip"
                onChange={e => setOtaFile(e.target.files[0])}
                className="hidden"
              />
            </label>

            {otaFile && (
              <div className="text-xs text-gray-400 space-y-0.5 text-center">
                <p>Selected: <span className="text-white font-medium">{otaFile.name}</span></p>
                <p>Size: <span className="text-white font-medium">{formatBytes(otaFile.size)}</span></p>
              </div>
            )}

            <button
              disabled={otaUploading}
              onClick={handleUpload}
              className="btn-primary w-full max-w-xs"
            >
              {otaUploading ? (
                <><FiRotateCcw className="animate-spin" /> Uploading…</>
              ) : (
                <><FiUpload /> Upload OTA Package</>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* OTA List */}
      <section className="card-chrome overflow-hidden p-0">
        {otaList.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">No OTA versions available.</p>
        ) : (
          <>
            <div className="w-full overflow-x-auto">
              <table className="min-w-full text-sm text-white">
                <thead className="bg-surface-inset text-xs uppercase tracking-wider text-gray-400">
                  <tr>
                    <th className="p-3 text-left font-medium">Device Model</th>
                    <th className="p-3 text-left font-medium">Update Type</th>
                    <th className="p-3 text-left font-medium">Availability</th>
                    <th className="p-3 text-left font-medium">Version</th>
                    <th className="p-3 text-left font-medium">Version Date</th>
                    <th className="p-3 text-left font-medium">URL</th>
                    <th className="p-3 text-left font-medium">Uploaded</th>
                    <th className="p-3 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {otaList.map((ota) => (
                    <tr
                      key={ota.deviceModel}
                      className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="p-3 font-medium">{ota.deviceModel}</td>

                      <td className="p-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          ota.updateType === 'major'
                            ? 'bg-red-500/15 text-red-400'
                            : 'bg-white/[0.05] text-gray-300'
                        }`}>
                          {ota.updateType.toUpperCase()}
                        </span>
                      </td>

                      <td className="p-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleUpdateState(ota);
                          }}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                            ota.updateState === 'ENABLED'
                              ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                              : 'bg-red-500/15 text-red-400 hover:bg-red-500/25'
                          }`}
                        >
                          {ota.updateState}
                        </button>
                      </td>

                      <td className="p-3">{ota.version}</td>

                      <td className="p-3 text-gray-400">
                        {(() => {
                          const match = ota.version?.match(/(\d{4})(\d{2})(\d{2})$/);
                          if (match) {
                            const [, year, month, day] = match;
                            return `${day}/${month}/${year}`;
                          }
                          return '—';
                        })()}
                      </td>

                      <td className="p-3">
                        {(() => {
                          const link = ota.url?.publicUrl ?? ota.url;
                          if (!link) return <span className="text-gray-500">N/A</span>;
                          const display = link.length > 50 ? `${link.slice(0, 50)}…` : link;
                          return (
                            <a
                              href={link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand-400 hover:text-brand text-xs break-all"
                            >
                              {display}
                            </a>
                          );
                        })()}
                      </td>

                      <td className="p-3 text-xs text-gray-400 whitespace-nowrap">
                        {ota.timestamp
                          ? new Date(ota.timestamp).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>

                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(ota)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/[0.05] border border-white/[0.08] text-gray-200 hover:bg-white/[0.08] text-xs font-medium transition-colors"
                          >
                            <FiEdit /> Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteCandidate({ version: ota.version, model: ota.deviceModel });
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-500/15 text-red-400 hover:bg-red-500/25 text-xs font-medium transition-colors"
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center mt-2 p-4">
                <nav className="flex items-center gap-1">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                  >
                    <FiChevronLeft />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => paginate(page)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'brand-pill'
                          : 'text-gray-400 hover:text-white hover:bg-white/[0.06]'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                  >
                    <FiChevronRight />
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </section>

      {/* Edit modal */}
      {editingOta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="card-chrome w-full max-w-lg p-0">
            <header className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <h3 className="inline-flex items-center gap-2 text-base font-semibold text-white">
                <FiEdit className="text-brand-400" /> Edit OTA {editVersion}
              </h3>
              <button
                onClick={handleClearOtaEditState}
                className="text-gray-500 hover:text-gray-200 p-1 rounded transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </header>

            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">
                    Version
                  </label>
                  <input
                    type="text"
                    value={editVersion}
                    onChange={(e) => setEditVersion(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <CustomDropdown
                    label="Device Model"
                    value={editDeviceModel}
                    onChange={setEditDeviceModel}
                    options={deviceModels.map((model) => ({ value: model, label: model }))}
                  />
                </div>
                <CustomDropdown
                  label="Update Is"
                  value={editOtaUpdateType}
                  onChange={setEditOtaUpdateType}
                  options={[
                    { label: 'Optional', value: 'optional' },
                    { label: 'Major', value: 'major' },
                  ]}
                />
                <CustomDropdown
                  label="Availability"
                  value={editOtaUpdateState}
                  onChange={setEditOtaUpdateState}
                  options={[
                    { label: 'Enabled', value: 'ENABLED' },
                    { label: 'Disabled', value: 'DISABLED' },
                  ]}
                />
              </div>

              <div className="card-inset p-4 flex flex-col sm:flex-row gap-4 items-center">
                <div className="w-16 h-16 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0">
                  <FiUpload className="text-gray-500 w-7 h-7" />
                </div>
                <div className="flex-1 w-full space-y-2">
                  <label className="btn-secondary cursor-pointer w-full">
                    <FiUpload /> Choose new .zip
                    <input
                      type="file"
                      accept=".zip"
                      onChange={(e) => setEditOtaFile(e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                  {editOtaFile && (
                    <div className="text-xs text-gray-400 space-y-0.5">
                      <p>Selected: <span className="text-white font-medium">{editOtaFile.name}</span></p>
                      <p>Size: <span className="text-white font-medium">{formatBytes(editOtaFile.size)}</span></p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
              <button onClick={handleClearOtaEditState} className="btn-secondary">Cancel</button>
              <button
                onClick={saveEditedOta}
                disabled={loadingEditMetadata || otaEditUploading}
                className="btn-primary"
              >
                {otaEditUploading ? 'Uploading…' : (loadingEditMetadata ? 'Extracting…' : 'Save')}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="card-chrome w-full max-w-md p-0">
            <header className="px-5 py-4 border-b border-white/[0.06]">
              <h3 className="inline-flex items-center gap-2 text-base font-semibold text-white">
                <FiTrash2 className="text-red-400" /> Confirm deletion
              </h3>
            </header>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-300">
                Delete OTA version{' '}
                <span className="text-white font-semibold">{deleteCandidate.version}</span>{' '}
                for{' '}
                <span className="text-white font-semibold">{deleteCandidate.model}</span>?
              </p>
              <p className="mt-2 text-xs text-gray-500">
                The .zip will be removed from Wasabi storage. This can't be undone.
              </p>
            </div>
            <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
              <button onClick={() => setDeleteCandidate(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={async () => {
                  const ota = deleteCandidate;
                  setDeleteCandidate(null);
                  await deleteOtaVersion(ota);
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
              >
                <FiTrash2 /> Delete
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

